import { Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/base/BackToTop';
import Seo from '@/components/base/Seo';

const values = [
  { icon: 'ri-heart-3-fill', title: '热爱动漫', desc: '因为热爱，所以用心。我们本身也是资深的追更党。' },
  { icon: 'ri-flashlight-fill', title: '及时准确', desc: '更新提醒力求第一时间送达，让追更更高效。' },
  { icon: 'ri-group-fill', title: '以用户为先', desc: '每一个功能都围绕追更体验打磨，拒绝花里胡哨。' },
  { icon: 'ri-leaf-fill', title: '陪伴成长', desc: '与百万追更读者一起，见证每一部作品的成长。' },
];

const stats = [
  { num: '2019', label: '成立于北京' },
  { num: '120万+', label: '注册追更读者' },
  { num: '3000+', label: '收录连载作品' },
  { num: '1000万+', label: '更新提醒送达' },
];

export default function About() {
  const [status, setStatus] = useState<'idle' | 'ok' | 'err'>('idle');
  const [formError, setFormError] = useState('');

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    if (String(fd.get('phone_alt') || '').trim()) {
      setStatus('ok');
      form.reset();
      return;
    }
    const payload = new FormData();
    fd.forEach((v, k) => {
      if (k !== 'phone_alt' && v) payload.append(k, v);
    });
    try {
      const res = await fetch('https://readdy.ai/api/form/da08qtu859p3u981e150', {
        method: 'POST',
        body: payload,
      });
      const text = await res.text();
      let parsed: any = null;
      try { parsed = JSON.parse(text); } catch { /* ignore */ }
      const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || text;
      if (res.ok && parsed?.code === 'OK') {
        setStatus('ok');
        form.reset();
      } else {
        setFormError(String(msg || '提交失败，请稍后再试'));
        setStatus('err');
      }
    } catch {
      setFormError('网络异常，请稍后再试');
      setStatus('err');
    }
  }

  return (
    <>
      <Seo
        title="关于我们 - 漫迹漫画追更工具 | 品牌介绍与联系方式"
        description="了解漫迹 MangaTrack 的品牌故事、价值观与口碑评价。我们致力于打造专业的漫画追更工具，提供漫画更新提醒、最新话推送与连载进度查询。如有任何问题欢迎联系我们。"
        path="/about"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于漫迹',
            description: '漫迹 MangaTrack 是一家专注于漫画追更工具与服务的内容平台。',
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://mangpush.github.io/' },
              { '@type': 'ListItem', position: 2, name: '关于我们' },
            ],
          },
        ]}
      />
      <Navbar />

      <main>
        {/* 品牌头图 */}
        <section className="relative flex min-h-[420px] w-full items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=stylized%20abstract%20art%20of%20an%20open%20book%20with%20colorful%20manga%20panels%20and%20sparkles%20flying%20out%2C%20warm%20orange%20and%20amber%20tones%2C%20inspiring%20warm%20atmosphere%2C%20clean%20modern%20illustration%2C%20no%20text&width=1600&height=600&seq=about-hero-2026&orientation=landscape"
            alt="关于漫迹品牌介绍背景图"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/55" />
          <div className="relative z-10 mx-auto flex w-full max-w-3xl flex-col items-center px-4 py-20 text-center">
            <h1 className="font-heading text-4xl text-background-50 md:text-5xl">关于漫迹 MangaTrack</h1>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-background-100 md:text-lg">
              我们相信，追更不应该是一件让人焦虑的事。漫迹的使命，就是让每一位漫画爱好者都能轻松掌握作品动态，追更不迷路。
            </p>
          </div>
        </section>

        {/* 品牌介绍 */}
        <section className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-4 py-1.5 text-sm font-medium text-primary-700">
                <i className="ri-building-2-fill" /> 我们的故事
              </span>
              <h2 className="mt-4 font-heading text-3xl text-foreground-950">从追更党到追更工具</h2>
              <p className="mt-4 text-[15px] leading-relaxed text-foreground-700">
                2019 年，几个热爱漫画的年轻人在北京创立了漫迹。最初，我们和所有追更党一样，每天被"今天某某更没更新"的焦虑困扰。正是这份真实的痛点，让我们决心打造一款真正懂追更的漫画追更工具。
              </p>
              <p className="mt-3 text-[15px] leading-relaxed text-foreground-700">
                如今的漫迹，已经成长为一个集漫画更新提醒、最新话推送、连载进度查询、漫画更新日历与追更社区于一体的追更平台，服务着超过 120 万名追更读者。
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                {['更新提醒', '最新话推送', '连载进度', '更新日历', '追更社区'].map((t) => (
                  <span key={t} className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-3 py-1.5 text-xs font-medium text-accent-800">
                    <i className="ri-check-line" /> {t}
                  </span>
                ))}
              </div>
            </div>
            <div className="overflow-hidden rounded-2xl">
              <img
                src="https://readdy.ai/api/search-image?query=stylized%20illustration%20of%20a%20cozy%20creative%20studio%20office%20with%20desks%2C%20manga%20posters%20and%20bookshelves%2C%20warm%20natural%20light%2C%20orange%20and%20cream%20tones%2C%20friendly%20working%20atmosphere%2C%20no%20text&width=1200&height=900&seq=about-team-2026&orientation=portrait"
                alt="漫迹创作团队工作场景"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* 数据 */}
        <section className="bg-primary-500 py-12">
          <div className="mx-auto grid max-w-7xl grid-cols-2 gap-8 px-4 md:grid-cols-4 md:px-6">
            {stats.map((s) => (
              <div key={s.label} className="text-center">
                <p className="font-heading text-3xl text-background-50">{s.num}</p>
                <p className="mt-1 text-sm text-background-100">{s.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 价值观 */}
        <section className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <div className="mb-8 text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-secondary-100 px-4 py-1.5 text-sm font-medium text-secondary-900">
              <i className="ri-quill-pen-fill" /> 品牌价值观
            </span>
            <h2 className="mt-3 font-heading text-3xl text-foreground-950">我们坚持什么</h2>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((v) => (
              <div key={v.title} className="rounded-lg border border-background-200 bg-background-100 p-6 text-center">
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-xl text-primary-600">
                  <i className={v.icon} />
                </span>
                <h3 className="mt-4 text-base font-bold text-foreground-950">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{v.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 口碑评价 */}
        <section className="bg-background-100/60 py-14 md:py-20">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <div className="mb-8 text-center">
              <span className="inline-flex items-center gap-2 rounded-full bg-accent-100 px-4 py-1.5 text-sm font-medium text-accent-700">
                <i className="ri-star-smile-fill" /> 口碑评价
              </span>
              <h2 className="mt-3 font-heading text-3xl text-foreground-950">追更读者怎么说</h2>
            </div>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
              {[
                { name: '阿橙', role: '追更 3 年', text: '用了漫迹之后再没漏过更新，更新提醒准时又贴心，强烈推荐给所有追更党！' },
                { name: '小鱼干', role: '漫画博主', text: '连载进度查询超方便，一部作品追到哪了清清楚楚，做内容再也不怕记错。' },
                { name: '凉皮', role: '全职读者', text: '更新日历太实用了，一周的安排一目了然，追更社区也特别有爱，氛围很好。' },
              ].map((t) => (
                <figure key={t.name} className="rounded-lg border border-background-200 bg-background-50 p-6">
                  <div className="flex text-secondary-500">
                    {Array.from({ length: 5 }).map((_, i) => <i key={i} className="ri-star-fill text-sm" />)}
                  </div>
                  <blockquote className="mt-4 text-sm leading-relaxed text-foreground-700">“{t.text}”</blockquote>
                  <figcaption className="mt-4 flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-500 text-sm font-bold text-background-50">
                      {t.name[0]}
                    </span>
                    <div>
                      <p className="text-sm font-bold text-foreground-950">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* 联系 */}
        <section className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-4 py-1.5 text-sm font-medium text-primary-700">
                <i className="ri-contacts-book-fill" /> 联系方式
              </span>
              <h2 className="mt-4 font-heading text-3xl text-foreground-950">随时欢迎联系我们</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                无论是合作、建议还是反馈，我们都很期待听到你的声音。我们会尽快回复每一封来信。
              </p>
              <ul className="mt-6 flex flex-col gap-4 text-sm text-foreground-700">
                <li className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-600"><i className="ri-mail-line" /></span>
                  hi@mangatrack.cn
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-100 text-accent-600"><i className="ri-wechat-line" /></span>
                  公众号：漫迹追更
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary-100 text-secondary-900"><i className="ri-bilibili-line" /></span>
                  B站：漫迹MangaTrack
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-background-200 text-foreground-700"><i className="ri-map-pin-line" /></span>
                  中国 · 北京市朝阳区
                </li>
              </ul>
            </div>

            <div className="rounded-xl border border-background-200 bg-background-100 p-6 md:p-8">
              <h3 className="font-heading text-xl text-foreground-950">给我们留言</h3>
              {status === 'ok' ? (
                <div className="mt-6 flex flex-col items-center gap-3 rounded-lg bg-accent-100 py-10 text-center">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-500 text-xl text-background-50"><i className="ri-check-line" /></span>
                  <p className="text-sm font-medium text-accent-800">提交成功，感谢你的留言，我们会尽快回复！</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form className="mt-6 flex flex-col gap-4">
                  <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="hp-field" />
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-foreground-800">姓名</label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="怎么称呼你"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-foreground-800">邮箱</label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="name@example.com"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-foreground-800">留言内容</label>
                    <textarea
                      name="message"
                      required
                      maxLength={500}
                      placeholder="想对我们说点什么……"
                      className="min-h-[120px] w-full resize-none rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                    />
                  </div>
                  <button
                    type="submit"
                    className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                  >
                    提交留言
                  </button>
                  {status === 'err' && formError && <p className="text-xs text-red-600">{formError}</p>}
                </form>
              )}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 pb-16 md:px-6">
          <Link to="/" className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700">
            <i className="ri-arrow-left-line" /> 返回首页
          </Link>
        </section>
      </main>

      <Footer />
      <BackToTop />
    </>
  );
}